import React from "react";
import moment from "moment";
import debounce from "lodash/debounce";
import withStyles from "isomorphic-style-loader/lib/withStyles";
import { graphql, compose } from "react-apollo";
import CalendarListings from "../CalendarListings.graphql";
import { FormattedMessage } from "react-intl";
import messages from "../../../locale/messages";
import s from "../Calendar.css";

import { getDates, getStartAndEndDate } from "../helpers";
import CalendarHeader from "./CalendarHeader";
import CalendarDateHeader from "./CalendarDateHeader";
import CalendarBody from "./CalendarBody";
import CalendarLegends from "./CalendarLegends";
import CalendarScrollButtons from "./CalendarScrollButtons";

class CalendarGrid extends React.Component {
  constructor(props) {
    super(props);
    this.scrollRef = React.createRef();
    this.state = {
      viewMode: "monthly",
      selectedDate: moment(),
      searchText: "",
      showSearchText: "",
      canScrollLeft: false,
      canScrollRight: true,
    };
    this.debouncedSetSearchText = debounce((value) => {
      this.setState({ searchText: value });
    }, 500);
  }

  componentDidMount() {
    this.updateScrollButtons();
    const scrollEl = this.scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener("scroll", this.updateScrollButtons);
    }
  }

  componentWillUnmount() {
    const scrollEl = this.scrollRef.current;
    if (scrollEl) {
      scrollEl.removeEventListener("scroll", this.updateScrollButtons);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.selectedDate !== this.state.selectedDate ||
      prevState.viewMode !== this.state.viewMode ||
      prevState.searchText !== this.state.searchText
    ) {
      const { selectedDate, viewMode } = this.state;
      const { calendarData } = this.props;
      const { startDate, endDate } = getStartAndEndDate(selectedDate, viewMode);

      if (calendarData && calendarData.refetch) {
        calendarData.refetch({
          searchKey: this.state.searchText,
          startDate,
          endDate,
        });
      }
    }
  }

  updateScrollButtons = () => {
    const el = this.scrollRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      this.setState({
        canScrollLeft: scrollLeft > 0,
        canScrollRight: scrollLeft + clientWidth < scrollWidth,
      });
    }
  };

  scrollLeft = () => {
    if (this.scrollRef.current) {
      this.scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  scrollRight = () => {
    if (this.scrollRef.current) {
      this.scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  handlePrev = () => {
    const { selectedDate, viewMode } = this.state;
    const newDate =
      viewMode === "daily"
        ? selectedDate.clone().subtract(1, "day")
        : viewMode === "weekly"
        ? selectedDate.clone().subtract(1, "week")
        : selectedDate.clone().subtract(1, "month");
    this.setState({ selectedDate: newDate });
  };

  handleNext = () => {
    const { selectedDate, viewMode } = this.state;
    const newDate =
      viewMode === "daily"
        ? selectedDate.clone().add(1, "day")
        : viewMode === "weekly"
        ? selectedDate.clone().add(1, "week")
        : selectedDate.clone().add(1, "month");
    this.setState({ selectedDate: newDate });
  };

  render() {
    const {
      viewMode,
      selectedDate,
      showSearchText,
      canScrollLeft,
      canScrollRight,
    } = this.state;

    const { calendarData } = this.props;
    const dates = getDates(viewMode, selectedDate);

    return (
      <div className={s.container}>
        <h1 className={s.availabilityText}>
          <FormattedMessage {...messages.calendar} />
        </h1>

        {/* Color legends */}
        <CalendarLegends />

        <div>
          <CalendarHeader
            selectedDate={selectedDate}
            handlePrev={this.handlePrev}
            handleNext={this.handleNext}
            loading={calendarData.loading}
            listings={calendarData?.CalendarListings?.results || []}
          />

          <div className={s.positionRelative}>
            <CalendarScrollButtons
              canScrollLeft={canScrollLeft}
              canScrollRight={canScrollRight}
              onLeftClick={this.scrollLeft}
              onRightClick={this.scrollRight}
            />

            <div ref={this.scrollRef} className={s.boderBox}>
              <CalendarDateHeader
                dates={dates}
                showSearchText={showSearchText}
                onSearchChange={(e) => {
                  const value = e.target.value;
                  this.setState({ showSearchText: value });
                  this.debouncedSetSearchText(value);
                }}
                canScrollLeft={canScrollLeft}
              />
              <CalendarBody
                loading={calendarData.loading}
                listings={calendarData?.CalendarListings?.results || []}
                viewMode={viewMode}
                canScrollLeft={canScrollLeft}
                dates={dates}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(s),
  graphql(CalendarListings, {
    name: "calendarData",
    options: () => {
      const today = moment();
      return {
        variables: {
          searchKey: "",
          startDate: today.startOf("month").format("YYYY-MM-DD"),
          endDate: today.endOf("month").format("YYYY-MM-DD"),
        },
        ssr: false,
        fetchPolicy: "network-only",
      };
    },
  })
)(CalendarGrid);
